import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { waLink } from "../lib/scroll";

const CHAPTERS = [
  {
    n: "01",
    title: "Small batch, by hand",
    body: "Every tray is mixed, shaped and baked in tiny batches from our kitchen in Dadar — never a production line. When a batch sells out, it's gone until the next oven.",
  },
  {
    n: "02",
    title: "Real ingredients only",
    body: "Butter, cream, good chocolate and time. Nothing artificial, nothing rushed — the kind of food you'd happily pull out of the oven at midnight.",
  },
  {
    n: "03",
    title: "Baked today, eaten today",
    body: "We bake to order, so your box leaves the oven the same day it reaches your table. Message us, tell us what you're craving, and we'll plan the rest.",
  },
];

export default function Manifesto() {
  return (
    <section id="manifesto" className="scroll-mt-24 py-24 sm:py-36">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16 sm:mb-24"
        >
          <div>
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-rust mb-4">
              The Manifesto
            </p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-xl">
              A bakery the size of <span className="italic text-rust">one oven.</span>
            </h2>
          </div>
          <p className="font-garamond italic text-xl sm:text-2xl text-mutedbrown max-w-xs leading-snug">
            “Nothing here is mass-produced. Everything here is personal.”
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Craft photo */}
          <motion.figure
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="overflow-hidden rounded-[24px] border-[4px] border-ink shadow-[10px_10px_0_#D4A359]">
              <motion.img
                src="/photos/focaccia-dough.jpg"
                alt="Focaccia dough dimpled with olive oil, black olives and sun-dried tomatoes"
                className="w-full aspect-[4/5] object-cover"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
            <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-mutedbrown flex justify-between">
              <span>Fig. 01 — Before the oven</span>
              <span>Dadar, Mumbai</span>
            </figcaption>
          </motion.figure>

          {/* Numbered chapters */}
          <div className="lg:col-span-7">
            {CHAPTERS.map((c, i) => (
              <motion.article
                key={c.n}
                data-testid="manifesto-chapter-card"
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.8, delay: i * 0.12 }}
                className="group border-t-[1.5px] border-cocoa/25 py-10 sm:py-12 first:border-t-[3px] first:border-ink"
              >
                <div className="flex items-start gap-6 sm:gap-10">
                  <span className="font-mono text-sm text-rust pt-2 border-[1.5px] border-rust rounded-full w-11 h-11 shrink-0 flex items-center justify-center group-hover:bg-rust group-hover:text-eggshell transition-colors duration-300">
                    {c.n}
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-2xl sm:text-4xl tracking-tight mb-3 group-hover:text-rust transition-colors duration-300">
                      {c.title}
                    </h3>
                    <p className="text-browntext leading-relaxed max-w-lg text-sm sm:text-base">
                      {c.body}
                    </p>
                  </div>
                </div>
              </motion.article>
            ))}

            <motion.a
              href={waLink("Hi Zoni's Bakehouse! I'd like to place an order.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="manifesto-order-link"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-3 mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-ink border-b-2 border-rust pb-2 hover:text-rust hover:gap-5 transition-all duration-300"
            >
              Start your order <ArrowUpRight size={15} />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
